import type { z } from 'zod'
import type * as C from '@habpro/contracts'

export type Item = z.infer<typeof C.PedagogicalItemDTO>
export type ItemRevision = z.infer<typeof C.PedagogicalItemRevisionDTO>
export type ItemBody = z.infer<typeof C.PedagogicalItemBodyInput>
export type ResourceInput = z.infer<typeof C.PedagogicalResourceInput>
export type Resource = z.infer<typeof C.PedagogicalResourceDTO>
export type AssessmentModel = z.infer<typeof C.AssessmentModelSchema>
export type Skill = z.infer<typeof C.SkillDTO>
export type Options = z.infer<typeof C.PedagogyOptionsDTO>
export type Template = z.infer<typeof C.CurriculumTemplateDTO>
export type TemplateRevision = z.infer<typeof C.CurriculumTemplateRevisionDTO>
export type TemplateInput = z.infer<typeof C.CurriculumTemplateCreateInput>
export type Structure = TemplateInput['structure']
export type CurriculumEntry = z.infer<typeof C.CurriculumEntryInput>
export type Blueprint = z.infer<typeof C.TaskBlueprintInput>
export type Plan = z.infer<typeof C.StudyPlanDTO>
export type PlanSession = z.infer<typeof C.StudyPlanSessionDTO>

export const kindLabels: Record<ItemBody['kind'], string> = {
  repertoire: 'Repertório',
  technique: 'Técnica',
  exercise: 'Exercício',
  listening: 'Escuta',
  theory: 'Teoria',
  creation: 'Criação',
  activity: 'Atividade',
  assessment: 'Avaliação'
}
export const sessionLabels: Record<PlanSession['kind'], string> = {
  lesson: 'Aula',
  review: 'Revisão',
  test: 'Prova',
  assessment: 'Avaliação',
  presentation: 'Apresentação'
}
export const resourceLabels: Record<ResourceInput['role'], string> = {
  score: 'Partitura',
  audio: 'Áudio',
  backing_track: 'Acompanhamento',
  video: 'Vídeo',
  image: 'Imagem',
  handout: 'Apostila',
  reference: 'Referência',
  other: 'Outro recurso'
}
export const timingLabels: Record<Blueprint['timing'], string> = {
  preparatory: 'Antes da aula',
  follow_up: 'Depois da aula',
  independent: 'Estudo independente'
}
export const emptyItem = (kind: ItemBody['kind'] = 'activity'): ItemBody => ({
  authorship: {
    origin: 'teacher',
    authorName: null,
    edition: null,
    sourceLabel: null
  },
  institutional: false,
  title: '',
  kind,
  objective: '',
  studentInstructions: '',
  description: '',
  teacherGuide: '',
  observationCriteria: '',
  prerequisites: '',
  durationMin: null,
  courseId: null,
  instrumentIds: [],
  levels: [],
  tags: [],
  skillIds: [],
  repertoire: kind === 'repertoire' ? emptyRepertoire() : null,
  assessment: null,
  resources: []
})
export const emptyRepertoire = (): NonNullable<ItemBody['repertoire']> => ({
  workTitle: '',
  composer: null,
  artist: null,
  arranger: null,
  musicalKey: null,
  timeSignature: null,
  bpm: null,
  durationSeconds: null,
  excerpt: null,
  hab: null
})
export const resourceToInput = (r: Resource): ResourceInput => ({
  id: r.id,
  role: r.role,
  title: r.title,
  caption: r.caption,
  audience: r.audience,
  target: r.target,
  sourceMaterialId: r.sourceMaterialId,
  sourceResourceId: r.sourceResourceId
})
/** Mudar o endereço cria outro recurso; a referência anterior pertence ao histórico. */
export function resourceWithUrl(
  resource: ResourceInput,
  url: string
): ResourceInput {
  if (resource.target.kind === 'url' && resource.target.url === url)
    return resource
  const {
    id: _id,
    sourceMaterialId: _material,
    sourceResourceId: _source,
    ...fields
  } = resource
  return { ...fields, target: { kind: 'url', url } }
}
export function itemToInput(revision: ItemRevision): ItemBody {
  const { classification: _classification, ...body } = revision.body
  return { ...body, resources: revision.resources.map(resourceToInput) }
}
export function templateToInput(t: Template): TemplateInput {
  const r = t.currentRevision
  return {
    authorship: r.authorship,
    institutional: r.institutional,
    visibility: t.visibility,
    title: r.title,
    description: r.description,
    courseId: r.course?.id ?? null,
    instrumentIds: r.instruments.map((i) => i.id),
    levels: r.levels,
    durationLabel: r.durationLabel,
    structure: r.structure
  }
}
export const moveAt = <T>(
  items: readonly T[],
  index: number,
  delta: number
): T[] => {
  const result = [...items],
    target = index + delta
  if (index < 0 || target < 0 || target >= result.length) return result
  ;[result[index], result[target]] = [result[target]!, result[index]!]
  return result
}
